import React from 'react';
import { Field, FieldProps } from 'formik';
import { MessageDescriptor } from 'react-intl';

import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { FormikCheckbox } from './formik-checkbox.component';
import { FormikFieldLabel } from './formik-field-label.component';
import { FieldCaption } from '../_ui/field-caption.component';

interface IFormikSettingCheckboxProps {
  name: string;
  fid?: string;
  // A descriptor routes through IntlMsg and keeps its `htmlFor` wiring; pass a
  // descriptor rather than a rendered <IntlMsg> element.
  label: string | MessageDescriptor;
  caption: React.ReactNode;
  // Binds a `true` / `false` / nullish value, rendering nullish as indeterminate.
  // See "`triState` renders `null` as indeterminate" in this folder's README.
  triState?: boolean;
}

// A setting toggle with a caption: a binary checkbox, or a tri-state one for an override that may be unset.
export const FormikSettingCheckbox: React.FC<IFormikSettingCheckboxProps> = ({
  name,
  fid = 'csf',
  label,
  caption,
  triState = false,
}) => {
  // Form-level prefix + form-unique name, so `id` and `htmlFor` never collide.
  // See "`fieldId` pairs `fid` with the field name" in this folder's README.
  const fieldId = [fid, name].filter(Boolean).join('-');
  const captionId = `${fieldId}-caption`;
  // MUI routes `inputProps` to the underlying <input>; a bare aria-describedby
  // would land on the wrapping button instead, where AT never reads it.
  const captionInputProps = { 'aria-describedby': captionId };

  return (
    <Box>
      {triState ? (
        <Field name={name}>
          {({ field, form }: FieldProps<boolean | null | undefined>) => {
            const indeterminate = field.value === null || field.value === undefined;
            return (
              <FormControl margin="normal" fullWidth component="div">
                <FormControlLabel
                  control={
                    <Checkbox
                      id={fieldId}
                      name={name}
                      checked={field.value === true}
                      indeterminate={indeterminate}
                      onChange={(event) => form.setFieldValue(name, event.target.checked)}
                      // MUI renders indeterminate as an icon plus a `data-` attribute
                      // only; `aria-checked="mixed"` is what a screen reader announces.
                      inputProps={indeterminate ? { ...captionInputProps, 'aria-checked': 'mixed' } : captionInputProps}
                    />
                  }
                  label={<FormikFieldLabel label={label} htmlFor={fieldId} />}
                />
              </FormControl>
            );
          }}
        </Field>
      ) : (
        <Field
          fid={fid}
          name={name}
          component={FormikCheckbox}
          label={label}
          inputProps={captionInputProps}
        />
      )}
      <FieldCaption id={captionId} underCheckbox>{caption}</FieldCaption>
    </Box>
  );
};
